'use client'

import { useEffect, useState, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, KeyRound, Loader2, ShieldAlert } from 'lucide-react'
import api, { login, getMe, getGoogleOAuthState, type ActiveSessionInfo } from '@/lib/api'
import { saveAuth, GOOGLE_OAUTH_STATE_KEY } from '@/lib/auth'
// Open-redirect guard for the ?next= deep-link param. After a successful
// sign-in we router.push(next), so an attacker could craft
// /login?next=https://evil.com (or the protocol-relative //evil.com, or a
// backslash variant) to bounce a freshly-authenticated user off-site for
// phishing. safeNextPath only honours same-origin ROOT-relative paths;
// anything that resolves to a different origin falls back to /dashboard.
// Hoisted to lib/safeNext.ts (INV-2) now that /signup + /verify-email +
// /team/accept-invite thread the same param.
import { safeNextPath, sanitizeNextPath } from '@/lib/safeNext'
import MarketingNav from '@/components/marketing/Nav'
import MarketingFooter from '@/components/marketing/Footer'
import TurnstileWidget, { isCaptchaRequired } from '@/components/ui/TurnstileWidget'
import TotpPrompt from '@/components/auth/TotpPrompt'

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''

// NB-F197 helper. Parse a `Retry-After` header value (either delta-seconds
// per RFC 7231 §7.1.3, or an HTTP-date) and return a human-readable wait
// phrase. Falls back to a vague "wait a minute" when the header is absent
// or unparseable so the user never sees "NaN seconds".
function formatRetryAfter(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') {
    return 'Wait a minute and try again.'
  }
  const str = String(raw).trim()
  let seconds = Number(str)
  if (!Number.isFinite(seconds)) {
    // HTTP-date form, e.g. "Wed, 21 Oct 2026 07:28:00 GMT"
    const epochMs = Date.parse(str)
    if (Number.isFinite(epochMs)) {
      seconds = Math.max(0, Math.round((epochMs - Date.now()) / 1000))
    } else {
      return 'Wait a minute and try again.'
    }
  }
  if (seconds <= 0) return 'Try again now.'
  if (seconds < 60) {
    return `Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`
  }
  const minutes = Math.ceil(seconds / 60)
  return `Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
}

// Honest error classification for sign-in. The previous catch block
// blanket-said "Incorrect email or password" for every failure, which
// turned 429 rate-limits, 500 server errors, and network drops into
// password-typo accusations, users then retry, hit the same wall, and
// blame their own keyboard. Each HTTP status now produces a message
// that points at the actual cause, with retry hints where they help.
function classifyLoginError(err: any): string {
  const status: number | undefined = err?.response?.status
  const detail = err?.response?.data?.detail

  // No HTTP response at all: network down, CORS preflight failed, DNS, etc.
  if (!status) {
    return 'Could not reach PulseSignal. Check your internet connection and try again. If this persists, email hello@pulsesignal.co.'
  }

  // FastAPI validators surface as 422 with a structured detail array.
  if (status === 422 && Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    const msg = typeof first?.msg === 'string' ? first.msg : ''
    return msg
      ? `${msg.replace(/^value is not a valid email address: /i, '')}`
      : 'That email or password looks invalid. Double-check the format and try again.'
  }

  // NB-F197: lockout. Backend returns 429 after N failed attempts with
  // Retry-After header. Accept both delta-seconds (RFC 7231 §7.1.3) and
  // HTTP-date forms. Surface minutes when >= 60s, seconds otherwise, so
  // the message stays human ("Try again in 5 minutes" vs "300 seconds").
  if (status === 429) {
    const raw = err?.response?.headers?.['retry-after']
    const wait = formatRetryAfter(raw)
    return `Too many sign-in attempts. ${wait}`
  }

  // 401 = actual credential mismatch OR token blocklisted mid-flight.
  if (status === 401) {
    if (typeof detail === 'string' && /blocked|revoked/i.test(detail)) {
      return 'Your previous session was ended. Sign in again.'
    }
    return 'Incorrect email or password. Try again, or use Continue with Google.'
  }

  // 403 = account locked / requires verification / SSO-enforced.
  if (status === 403) {
    if (typeof detail === 'string' && detail.length > 0) return detail
    return 'Your account cannot sign in via password right now. If your team uses SSO, click Continue with SSO. Otherwise email hello@pulsesignal.co.'
  }

  // 5xx = server side. Tell the user it is not their fault.
  if (status >= 500) {
    return 'PulseSignal is having trouble on our end. We have logged it; please try again in a minute, or email hello@pulsesignal.co.'
  }

  // Catch-all: keep the original backend detail if it is a clean string.
  if (typeof detail === 'string' && detail.length > 0) return detail
  return `Sign-in failed (HTTP ${status}). Try again or email hello@pulsesignal.co.`
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  // NB-F195: Cloudflare Turnstile token. Empty string until the widget
  // succeeds (or always empty when no site key is provisioned, in which
  // case isCaptchaRequired() returns false and we skip the gate).
  const [turnstileToken, setTurnstileToken] = useState('')
  // v137 fail-open: true when the Turnstile widget itself ERRORED (bad
  // site key, Cloudflare outage, adblocker). In that state we let the
  // submit through with no token and the server decides; otherwise a
  // broken widget would hard-brick sign-in client-side.
  const [turnstileFailed, setTurnstileFailed] = useState(false)
  // Single-session conflict-prompt state. When the backend returns
  // session_conflict, this dialog opens; the "Take over" button retries
  // login with force_takeover=true.
  const [conflict, setConflict] = useState<{
    sessions: ActiveSessionInfo[]
    message: string
  } | null>(null)
  const [takingOver, setTakingOver] = useState(false)
  // SEC-2FA-3: when the backend returns 2fa_required, show the TOTP prompt
  // and re-POST the same credentials plus the entered code.
  const [twofa, setTwofa] = useState(false)
  const [twofaSubmitting, setTwofaSubmitting] = useState(false)
  const [twofaError, setTwofaError] = useState<string | null>(null)
  // Escape closes the conflict-prompt dialog (matches the Cancel button).
  // Keeps keyboard users from being stuck in the modal.
  useEffect(() => {
    if (!conflict || takingOver) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setConflict(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [conflict, takingOver])
  // SSO sign-in. When the user clicks "Continue with SSO" we POST the
  // email to /auth/sso/initiate. If the domain has an enabled connection
  // the backend returns a redirect URL; otherwise we surface a "no SSO
  // configured for that domain" banner and the user can fall back to the
  // password form.
  const [ssoLoading, setSsoLoading] = useState(false)
  const [ssoNote, setSsoNote] = useState<string | null>(null)
  // ?sso_error= from the callback path (idp_rejected, sso_disabled,
  // invalid_state, etc.) so we can surface a tailored banner.
  const ssoErrorParam = searchParams.get('sso_error')
  const ssoErrorBanner =
    ssoErrorParam === 'idp_rejected'
      ? 'Your identity provider rejected the sign-in. Please contact your administrator.'
      : ssoErrorParam === 'sso_disabled'
      ? 'SSO is currently disabled for your organization. Please contact your administrator.'
      : ssoErrorParam === 'sso_not_configured'
      ? 'SSO is not configured on this environment. Please contact your administrator.'
      : ssoErrorParam === 'invalid_state'
      ? 'The SSO sign-in link expired. Please try again.'
      : ssoErrorParam
      ? 'SSO sign-in failed. Please try again or use your password.'
      : null

  // INV-2: the signup CTA must carry ?next= forward. A seat invitee who
  // lands on /login?next=/team/accept-invite?token=… but has no account
  // yet clicks "Create your account" below; a plain /signup link dropped
  // the invite token and stranded them in the paid signup funnel.
  const nextParam = sanitizeNextPath(searchParams.get('next'))
  const signupHref = nextParam
    ? `/signup?next=${encodeURIComponent(nextParam)}`
    : '/signup'

  // B3: banner above the form explaining why the user got kicked, if
  // they did. Driven by the ?session_ended= query the 401 interceptor
  // appends when redirecting to /login.
  const sessionEndedKind = searchParams.get('session_ended')
  const sessionEndedBanner =
    sessionEndedKind === 'elsewhere'
      ? 'You were logged out because someone signed in on another device.'
      : sessionEndedKind === 'upgrade'
      ? 'Please sign in again to upgrade your session security.'
      : null

  const finishLogin = async (accessToken: string, mustEnroll2fa = false) => {
    // Atomic token+user save: fetch /auth/me with the just-received token
    // passed explicitly, THEN commit both to localStorage. Eliminates the
    // half-state window where the token is committed but the user object
    // isn't, which left users in a phantom-authenticated limbo if getMe
    // failed or the tab closed mid-login. (AUDITS 17_signin_deep_dive.md)
    const user = await getMe(accessToken)
    saveAuth({ access_token: accessToken, token_type: 'bearer' }, user)
    // SEC-2FA-3: an org that mandates 2FA routes un-enrolled members into
    // setup before anything else (the user is authenticated; the route guard
    // there finishes the posture without the chicken-and-egg of blocking the
    // very login needed to enroll).
    if (mustEnroll2fa) {
      router.push('/dashboard/settings/security?enroll2fa=1')
      return
    }
    const next = safeNextPath(searchParams.get('next'))
    router.push(next)
  }

  // In-flight guard so a double-clicked Sign in button doesn't fire two
  // parallel login() calls. The second one would race against the first
  // and could produce a false "another session active" prompt because
  // each call counts as a distinct device login attempt on the backend.
  const inFlightRef = (typeof window !== 'undefined' && (window as { __ps_login_inflight?: boolean })) || null

  const doLogin = async (forceTakeover: boolean) => {
    if (inFlightRef && inFlightRef.__ps_login_inflight) return
    if (inFlightRef) inFlightRef.__ps_login_inflight = true
    try {
      const resp = await login({
        email,
        password,
        force_takeover: forceTakeover,
        // NB-F195: forward Turnstile token when present. Empty string is
        // fine: the backend ignores the field when its secret is unset.
        turnstile_token: turnstileToken || undefined,
      })
      if (resp.status === 'session_conflict') {
        setConflict({ sessions: resp.active_sessions, message: resp.message })
        return
      }
      if (resp.status === '2fa_required') {
        // SEC-2FA-3: surface the TOTP prompt; submitTotp re-POSTs the same
        // credentials plus the code. (Without this branch the old code fell
        // through to finishLogin(undefined) and 2FA login was unusable.)
        setTwofaError(null)
        setTwofa(true)
        return
      }
      setConflict(null)
      await finishLogin(resp.access_token, resp.must_enroll_2fa)
    } finally {
      if (inFlightRef) inFlightRef.__ps_login_inflight = false
    }
  }

  // SEC-2FA-3: complete a 2fa_required password login by re-POSTing the
  // credentials with the entered authenticator/recovery code.
  const submitTotp = async (code: string) => {
    setTwofaSubmitting(true)
    setTwofaError(null)
    try {
      const resp = await login({
        email,
        password,
        totp_code: code,
        turnstile_token: turnstileToken || undefined,
      })
      if (resp.status === '2fa_required') {
        // Shouldn't recur (we sent a code) but guard against a loop.
        setTwofaError('That code did not verify. Try again.')
        return
      }
      if (resp.status === 'session_conflict') {
        setTwofa(false)
        setConflict({ sessions: resp.active_sessions, message: resp.message })
        return
      }
      setTwofa(false)
      await finishLogin(resp.access_token, resp.must_enroll_2fa)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } } }
      const detail = e?.response?.data?.detail
      setTwofaError(
        typeof detail === 'string'
          ? detail
          : 'That code did not verify. Check your authenticator and try again.',
      )
    } finally {
      setTwofaSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError(null)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.')
      return
    }
    if (!password) {
      setError('Enter your password.')
      return
    }
    // NB-F195: block submit until Turnstile verifies. Only enforced
    // when the site key env is set, so dev / preview without a key still
    // works end-to-end. v137: if the widget itself ERRORED we fail open
    // (submit with no token, server decides) instead of bricking sign-in.
    if (isCaptchaRequired() && !turnstileToken && !turnstileFailed) {
      setError('Please complete the verification below before signing in.')
      return
    }
    setLoading(true)
    try {
      await doLogin(false)
    } catch (err: any) {
      localStorage.removeItem('access_token')
      // v114 onboarding-audit finding #1: an unverified user who tries to LOG IN
      // gets 403 EMAIL_NOT_VERIFIED. Don't strand them on the raw enum string with
      // no recovery path. Route them to the OTP page, which pre-fills their email
      // and offers a resend, so they can finish verifying. (The happy signup path
      // keeps users on the OTP step and is unaffected.) The finally still runs.
      if (
        err?.response?.status === 403 &&
        err?.response?.data?.detail === 'EMAIL_NOT_VERIFIED'
      ) {
        // v228 M-ONBOARD-MISC (P2): preserve the plan/billing/next intent on
        // the login→verify redirect. A user who signed up via
        // /signup?plan=pro&billing=annual (which threads plan→verify→checkout)
        // but tried to log in before verifying would otherwise lose that
        // intent and fall through to /select-plan to re-pick. verify-email
        // reads plan/billing/next and routes to /checkout with them preserved.
        let verifyHref = `/verify-email?email=${encodeURIComponent(email.trim())}`
        const extra = new URLSearchParams()
        const planParam = searchParams.get('plan')
        const billingParam = searchParams.get('billing')
        if (planParam) extra.set('plan', planParam)
        if (billingParam) extra.set('billing', billingParam)
        if (nextParam) extra.set('next', nextParam)
        const extraQs = extra.toString()
        if (extraQs) verifyHref += `&${extraQs}`
        router.push(verifyHref)
        return
      }
      setError(classifyLoginError(err))
      // Surface the full error in devtools so operator / user can paste
      // it back to support without having to inspect the network tab.
      if (typeof console !== 'undefined') {
        console.error('[login] sign-in failed:', {
          status: err?.response?.status,
          detail: err?.response?.data?.detail,
          message: err?.message,
          retryAfter: err?.response?.headers?.['retry-after'],
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTakeover = async () => {
    setError(null)
    setTakingOver(true)
    try {
      await doLogin(true)
    } catch (err: any) {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      let msg: string
      if (typeof detail === 'string' && detail.length > 0) {
        msg = `Take-over failed (${status ?? 'network'}): ${detail}`
      } else if (status) {
        msg = `Take-over failed (HTTP ${status}). Please re-enter your password and try again.`
      } else {
        msg = 'Take-over failed: could not reach the server. Check your connection and retry.'
      }
      setError(msg)
      setConflict(null)
    } finally {
      setTakingOver(false)
    }
  }

  const handleSsoSignIn = async () => {
    if (!email) {
      setError('Enter your email above first, then click Continue with SSO.')
      return
    }
    setSsoNote(null)
    setError(null)
    setSsoLoading(true)
    try {
      const resp = await api.post('/auth/sso/initiate', { email })
      const data = resp.data
      if (data?.sso_required && data?.redirect) {
        window.location.href = data.redirect
        return
      }
      setSsoNote(
        'No SSO configured for that email domain. Use your password to sign in.',
      )
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      let msg = 'SSO sign-in failed. Please try again.'
      if (typeof detail === 'string') msg = detail
      else if (detail && typeof detail === 'object' && detail.message) msg = detail.message
      setError(msg)
    } finally {
      setSsoLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID || googleLoading) return
    setGoogleLoading(true)
    const redirectUri = `${window.location.origin}/auth/google/callback`
    // SEC-AUTH: ask the backend for a signed OAuth state, stash it for the
    // callback to compare against, and forward it to Google. If the backend
    // does not issue one (feature off / older image) we fall through with no
    // state so sign-in still works — the backend only enforces state when the
    // param is present and GOOGLE_OAUTH_STATE_ENFORCE is on.
    const state = await getGoogleOAuthState()
    const paramsInit: Record<string, string> = {
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
    }
    if (state) {
      try {
        sessionStorage.setItem(GOOGLE_OAUTH_STATE_KEY, state)
      } catch {
        // sessionStorage unavailable (private mode / disabled); proceed
        // without stashing — the callback will skip validation gracefully.
      }
      paramsInit.state = state
    }
    const params = new URLSearchParams(paramsInit)
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
  }

  return (
    <main className="bg-cream-100 text-ink-900 min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-terra-100/40" />
        <div className="absolute top-[30%] right-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-olive-100/40" />
        <div className="absolute bottom-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-3xl bg-petrol-100/30" />
      </div>

      <MarketingNav />

      <section className="editorial relative pt-28 sm:pt-36 pb-20">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-h-xl font-semibold tracking-[-0.035em] leading-[0.98] text-ink-900 text-balance">
              Sign in
            </h1>
            <p className="lede mt-6 text-ink-700 text-pretty">
              Sign in to your PulseSignal account.
            </p>
          </div>

          <div className="bg-cream-50 border border-ink-900/10 rounded-2xl p-8 shadow-sm">
            {sessionEndedBanner && (
              <div className="flex items-start gap-2 bg-petrol-100/60 border border-petrol-300/40 text-ink-900 rounded-lg p-3 mb-6 text-sm">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{sessionEndedBanner}</span>
              </div>
            )}

            {ssoErrorBanner && (
              <div className="flex items-start gap-2 bg-maroon-500/10 border border-maroon-500/30 text-maroon-600 rounded-lg p-3 mb-6 text-sm" role="alert">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{ssoErrorBanner}</span>
              </div>
            )}

            {ssoNote && (
              <div className="flex items-start gap-2 bg-petrol-100/60 border border-petrol-300/40 text-ink-900 rounded-lg p-3 mb-6 text-sm">
                <KeyRound className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{ssoNote}</span>
              </div>
            )}

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="flex items-center gap-2 bg-maroon-500/10 border border-maroon-500/30 text-maroon-600 rounded-lg p-3 mb-6 text-sm overflow-hidden"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {GOOGLE_CLIENT_ID && (
              <>
                <motion.button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  whileHover={{ y: -1, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="w-full flex items-center justify-center gap-3 bg-cream-50 hover:border-ink-900/20 disabled:opacity-70 text-ink-900 font-medium py-3 px-4 rounded-lg border border-ink-900/10 transition-colors text-sm mb-5"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-ink-700" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  )}
                  {googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
                </motion.button>

                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-ink-900/10" />
                  <span className="text-eyebrow uppercase tracking-widest text-ink-600 font-semibold">or sign in with email</span>
                  <div className="flex-1 h-px bg-ink-900/10" />
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {[
                { label: 'Email address', type: 'email', value: email, onChange: setEmail, placeholder: 'you@company.com' },
                { label: 'Password', type: 'password', value: password, onChange: setPassword, placeholder: 'Your password' },
              ].map(({ label, type, value, onChange, placeholder }, i) => {
                const fieldId = `login-${type}`
                return (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <label htmlFor={fieldId} className="text-eyebrow uppercase tracking-widest text-ink-600 font-semibold block mb-2">{label}</label>
                  <input
                    id={fieldId}
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required
                    aria-label={label}
                    className="cream-input"
                  />
                </motion.div>
                )
              })}

              <div className="flex justify-end -mt-1">
                <Link
                  href="/forgot-password"
                  className="text-xs text-ink-600 hover:text-terra-600 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* NB-F195: Cloudflare Turnstile. Renders null when no site key. */}
              <TurnstileWidget
                action="login"
                id="login-turnstile"
                onVerify={setTurnstileToken}
                onErrorStateChange={setTurnstileFailed}
              />

              <motion.button
                type="submit"
                disabled={loading || (isCaptchaRequired() && !turnstileToken && !turnstileFailed)}
                whileHover={{ y: -1, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </motion.button>

              {/* Scale-tier SSO. */}
              <motion.button
                type="button"
                onClick={handleSsoSignIn}
                disabled={ssoLoading}
                whileHover={{ y: -1, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full flex items-center justify-center gap-3 bg-cream-50 hover:border-ink-900/20 disabled:opacity-70 text-ink-900 font-medium py-3 px-4 rounded-lg border border-ink-900/10 transition-colors text-sm"
              >
                {ssoLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4" />
                )}
                {ssoLoading ? 'Checking SSO...' : 'Continue with SSO'}
              </motion.button>
            </form>

            <div className="my-6 h-px bg-ink-900/10" />

            <p className="text-center text-sm text-ink-700">
              Don&apos;t have an account?{' '}
              <Link href={signupHref} className="text-terra-500 hover:opacity-80 font-medium transition-opacity">
                {nextParam ? 'Create your account' : 'Start a 14-day trial'}
              </Link>
            </p>
          </div>

          <p className="text-caption text-center text-ink-600 mt-6">
            By signing in, you agree to our{' '}
            <Link href="/terms" className="text-ink-900 hover:opacity-80 transition-opacity">Terms</Link>
            {' '}and{' '}
            <Link href="/privacy" className="text-ink-900 hover:opacity-80 transition-opacity">Privacy Policy</Link>
          </p>
        </div>
      </section>

      {/* Single-session conflict-prompt dialog */}
      <AnimatePresence>
        {conflict && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="b3-conflict-title"
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              className="bg-cream-50 border border-ink-900/10 rounded-2xl p-6 max-w-md w-full shadow-xl"
            >
              <h2 id="b3-conflict-title" className="text-lg font-semibold text-ink-900 mb-2">
                Another session is signed in
              </h2>
              <p className="text-sm text-ink-700 mb-4">{conflict.message}</p>
              <ul className="space-y-2 mb-5">
                {conflict.sessions.map((s) => (
                  <li
                    key={s.id}
                    className="text-xs text-ink-700 bg-cream-100/80 border border-ink-900/10 rounded-lg p-3"
                  >
                    <div className="font-medium text-ink-900 truncate">{s.device}</div>
                    <div className="text-ink-600 mt-0.5">
                      Last active{' '}
                      {s.last_active ? new Date(s.last_active).toLocaleString() : 'recently'}
                      {s.fingerprint ? ` · device ${s.fingerprint}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setConflict(null)}
                  className="px-4 py-2 rounded-lg border border-ink-900/15 text-ink-900 text-sm hover:bg-cream-100 transition-colors"
                  disabled={takingOver}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleTakeover}
                  className="px-4 py-2 rounded-lg bg-terra-500 text-cream-50 text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-70"
                  disabled={takingOver}
                >
                  {takingOver && <Loader2 className="w-4 h-4 animate-spin" />}
                  Take over and log them out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {twofa && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sec-2fa-title"
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              className="bg-cream-50 border border-ink-900/10 rounded-2xl p-6 max-w-md w-full shadow-xl"
            >
              <TotpPrompt
                onSubmit={submitTotp}
                submitting={twofaSubmitting}
                error={twofaError}
                onCancel={() => {
                  setTwofa(false)
                  setTwofaError(null)
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MarketingFooter />

      <style jsx>{`
        .cream-input {
          width: 100%;
          background: #fbf7ec;
          border: 1px solid rgba(11, 12, 16, 0.12);
          border-radius: 10px;
          padding: 10px 14px;
          color: #0b0c10;
          /* 16px floor prevents iOS Safari auto-zoom on focus. */
          font-size: 16px;
          transition: border-color .15s, box-shadow .15s;
        }
        .cream-input::placeholder { color: rgba(11,12,16,0.40); }
        .cream-input:focus {
          outline: none;
          border-color: rgba(199, 93, 44, 0.55);
          box-shadow: 0 0 0 3px rgba(199, 93, 44, 0.15);
        }
      `}</style>
    </main>
  )
}
